/**
 * Content tag bundle definitions
 * @module content/fields
 */
import * as Yup from 'yup'
import { List, Map, Set } from 'immutable'
import defaultTo from 'lodash/defaultTo'
import {
  CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_CONSCIOUS_FORMAT,
  CONTENT_REFERENCE_TYPE_CONSCIOUS_TOPIC,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
  CONTENT_REFERENCE_TYPE_FILM_SUBJECT,
  CONTENT_REFERENCE_TYPE_FILM_TYPE,
  CONTENT_REFERENCE_TYPE_FITNESS_DURATION,
  CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR,
  CONTENT_REFERENCE_TYPE_FITNESS_LEVEL,
  CONTENT_REFERENCE_TYPE_FITNESS_SPECIALTY,
  CONTENT_REFERENCE_TYPE_FITNESS_STYLE,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_ONBOARDING_CHOICES,
  CONTENT_REFERENCE_TYPE_RECIPE_DIET,
  CONTENT_REFERENCE_TYPE_RECIPE_TYPE,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_VIDEO_COLLECTION,
  CONTENT_REFERENCE_TYPE_VIDEO_TOPIC,
  CONTENT_REFERENCE_TYPE_YOGA_DURATION,
  CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
  CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
  CONTENT_REFERENCE_TYPE_YOGA_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
  CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
  CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
  CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
  CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_PRODUCT,
} from '../content-references/types'
import { getListCriteriaInitialValue } from '../../gui/components/content-detail/helpers'
import {
  CONTENT_TYPE_FAQ,
  CONTENT_TYPE_GUIDE,
  CONTENT_TYPE_GUIDE_DAY,
  CONTENT_TYPE_PERSON,
  CONTENT_TYPE_PROMOTION,
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_TAG,
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_PRODUCT,
  CONTENT_TYPE_SERVICE,
  CONTENT_TYPE_BONUS_PACKAGE,
} from './content-types'
import { getDisplayTypes } from './display-types'
import {
  PRODUCT_TYPE_EPISODE,
  PRODUCT_TYPE_EPISODIC,
  PRODUCT_TYPE_SEGMENT,
  PRODUCT_TYPE_SEGMENTED,
  PRODUCT_TYPE_SINGLE,
} from './product-types'

const displayTypes = getDisplayTypes()
const productTypesVideo = [PRODUCT_TYPE_EPISODE, PRODUCT_TYPE_SEGMENT, PRODUCT_TYPE_SINGLE]
const productTypesSeries = [PRODUCT_TYPE_EPISODIC, PRODUCT_TYPE_SEGMENTED]
const episodeProductTypes = Set([PRODUCT_TYPE_EPISODE, PRODUCT_TYPE_SEGMENT])

// Add a custom validation function for async fields
const asyncFieldSchema = (isMulti) => {
  if (isMulti) {
    return Yup.array()
      .nullable()
      .transform((value) => {
        if (!value) return []
        if (value.value && Array.isArray(value.value)) {
          return value.value.map((v) => Number(v))
        }
        if (Array.isArray(value)) {
          return value.map((v) => Number(v))
        }
        return []
      })
  }

  return Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      // Always prioritize originalValue if it exists and is an object
      if (originalValue && typeof originalValue === 'object' && 'value' in originalValue) {
        const numValue = Number(originalValue.value)
        return Number.isNaN(numValue) ? null : numValue
      }

      // Only use value if it's not NaN and originalValue doesn't exist
      if (value !== null && value !== undefined && !Number.isNaN(value)) {
        const numValue = Number(value)
        return Number.isNaN(numValue) ? null : numValue
      }

      return null
    })
}

const baseContentSchema = Yup.object({
  title: Yup.string()
    .max(255, 'Over the 255 character limit. Shorten this before publishing.')
    .required('Required'),
  thirdPartyDescription: Yup.string()
    .max(255, 'Over the 255 character limit. Shorten this before publishing.')
    .nullable(),
  instructor: Yup.string().max(255, 'Must be less than 255 characters').nullable(),
  director: Yup.string().max(255, 'Must be less than 255 characters').nullable(),
  producer: Yup.string().max(255, 'Must be less than 255 characters').nullable(),
  writer: Yup.string().max(255, 'Must be less than 255 characters').nullable(),
  cast: Yup.string().max(1024, 'Must be less than 1024 characters').nullable(),
  copyright: Yup.string().max(255, 'Must be less than 255 characters').nullable(),
  studio: Yup.string().max(255, 'Must be less than 255 characters').nullable(),
  shortDescription: Yup.string()
    .max(150, 'Over the 150 character limit. Shorten this before publishing.')
    .nullable(),
  [CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY]: Yup.number().nullable(),
  [CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN]: Yup.number().nullable(),
  [CONTENT_REFERENCE_TYPE_CENTERS]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_MARKETING_CENTERS]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_INTERVIEW_HOST]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_YOGA_TEACHER]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_ONBOARDING_CHOICES]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_CATEGORY]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_FILM_SUBJECT]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_FILM_TYPE]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_GENRES]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_YOGA_DURATION]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_YOGA_FOCUS]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_YOGA_LEVEL]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_YOGA_STYLE]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_FITNESS_DURATION]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_FITNESS_LEVEL]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_FITNESS_SPECIALTY]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_FITNESS_STYLE]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_CONSCIOUS_FORMAT]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_CONSCIOUS_TOPIC]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_MEDITATION_STYLE]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_MEDITATION_DURATION]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_RECIPE_TYPE]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_RECIPE_DIET]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY]: Yup.number().nullable(),
  [CONTENT_REFERENCE_TYPE_PRODUCT]: asyncFieldSchema(true),
  [CONTENT_REFERENCE_TYPE_VIDEO_COLLECTION]: Yup.array().of(Yup.number().nullable()),
  [CONTENT_REFERENCE_TYPE_VIDEO_TOPIC]: Yup.array().of(Yup.number().nullable()),
})

const CONTENT_DETAIL_SCHEMAS = Map({
  content: baseContentSchema,
  [CONTENT_TYPE_FAQ]: baseContentSchema,
  [CONTENT_TYPE_PRODUCT]: baseContentSchema,
  [CONTENT_TYPE_SERVICE]: baseContentSchema,
  [CONTENT_TYPE_BONUS_PACKAGE]: baseContentSchema,
  [CONTENT_TYPE_GUIDE]: baseContentSchema,
  [CONTENT_TYPE_GUIDE_DAY]: baseContentSchema,
  [CONTENT_TYPE_TAG]: baseContentSchema.shape({
    filterType: Yup.string().nullable(),
    bundle: Yup.string().nullable(),
  }),
  [CONTENT_TYPE_PERSON]: baseContentSchema.shape({
    filterType: Yup.string().nullable(),
    bundle: Yup.string().nullable(),
  }),
  [CONTENT_TYPE_ARTICLE]: baseContentSchema.shape({
    episodeNumber: Yup.number().when('productType', {
      is: (v) => episodeProductTypes.includes(v),
      then: Yup.number().required('Episode number (wordpress ID) required for articles'),
      otherwise: Yup.number().nullable(),
    }),
  }),
  [CONTENT_TYPE_VIDEO]: baseContentSchema.shape({
    studio: Yup.string().max(255, 'Must be less than 255 characters').nullable(),
    productType: Yup.string()
      .transform((value) => (value === null ? '' : value))
      .required('Please select a product type')
      .oneOf(productTypesVideo, 'Please select a valid product type'),
    displayType: Yup.string()
      .transform((value) => (value === null ? '' : value))
      .required('Please select a display type')
      .oneOf(displayTypes, 'Please select a valid display type'),
    seasonNumber: Yup.number()
      .transform((value) => {
        if (value === null || value === '' || value === undefined) return null
        const num = Number(value)
        return Number.isNaN(num) ? null : num
      })
      .when('productType', {
        is: (v) => episodeProductTypes.includes(v),
        then: Yup.number()
          .required('Season number is required for episodes and segments')
          .typeError('Season number must be a valid number'),
        otherwise: Yup.number().nullable().typeError('Season number must be a valid number'),
      }),
    episodeNumber: Yup.number()
      .transform((value) => {
        if (value === null || value === '' || value === undefined) return null
        const num = Number(value)
        return Number.isNaN(num) ? null : num
      })
      .when('productType', {
        is: (v) => episodeProductTypes.includes(v),
        then: Yup.number()
          .required('Episode number is required for episodes and segments')
          .typeError('Episode number must be a valid number'),
        otherwise: Yup.number().nullable().typeError('Episode number must be a valid number'),
      }),
    [CONTENT_REFERENCE_TYPE_SERIES]: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        // If we have an object with value property, use that
        if (originalValue?.value) {
          return Number(originalValue.value)
        }
        // If we have a direct number value, use that
        if (typeof value === 'number' && !Number.isNaN(value)) {
          return value
        }
        return null
      })
      .when('productType', {
        is: (v) => episodeProductTypes.includes(v),
        then: (schema) => schema.required('Series required for episodes and segments'),
        otherwise: (schema) => schema.nullable(),
      }),
    [CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY]: Yup.number()
      .nullable()
      .transform((value) => (value === null ? undefined : value))
      .required('Admin category is required'),
    [CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN]: Yup.number()
      .nullable()
      .transform((value) => (value === null ? undefined : value))
      .required('Content origin is required'),
    [CONTENT_REFERENCE_TYPE_GENRES]: Yup.array()
      .nullable()
      .transform((value) => (!value || value.length === 0 ? undefined : value))
      .required('At least one Genre is required')
      .min(1, 'At least one Genre required for content type video'),
  }),
  [CONTENT_TYPE_SERIES]: baseContentSchema.shape({
    productType: Yup.string()
      .required('Required')
      .oneOf(productTypesSeries, 'Must be a valid product type'),
    displayType: Yup.string()
      .required('Required')
      .oneOf(displayTypes, 'Must be a valid display type'),
    productionStatus: Yup.string().nullable(),
    [CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY]: Yup.number().required('Admin category is required'),
    [CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN]: Yup.number().required('Content origin is required'),
    [CONTENT_REFERENCE_TYPE_GENRES]: Yup.array().min(
      1,
      'At least one Genre required for content type series',
    ),
  }),
  [CONTENT_TYPE_PROMOTION]: baseContentSchema.shape({
    ctaLabel: Yup.string().nullable(),
    ctaPath: Yup.string().nullable(),
    ctaDeeplinkPath: Yup.string().nullable(),
  }),
})

export function getValidationSchema (content, section = null) {
  return CONTENT_DETAIL_SCHEMAS.get(defaultTo(section, content.get('contentType')))
}

function getContentReferenceValueByType (content, type) {
  const contentReference = content
    .get('references', List())
    .filter((r) => r.get('type') === type)
    .first()
  return contentReference && contentReference.has('contentId')
    ? contentReference.get('contentId')
    : ''
}

function getContentReferenceValuesByType (content, type) {
  return (
    content
      .get('references', List())
      .filter((r) => r.get('type') === type)
      .map((r) => r.get('contentId'))
      .toArray() || []
  )
}

function getContentReferences (content) {
  return {
    [CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY]: getContentReferenceValueByType(
      content,
      CONTENT_REFERENCE_TYPE_ADMIN_CATEGORY,
    ),
    [CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN]: getContentReferenceValueByType(
      content,
      CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
    ),
    [CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY]: getContentReferenceValueByType(
      content,
      CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
    ),
    [CONTENT_REFERENCE_TYPE_SERIES]: getContentReferenceValueByType(
      content,
      CONTENT_REFERENCE_TYPE_SERIES,
    ),
    [CONTENT_REFERENCE_TYPE_CENTERS]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_CENTERS,
    ),
    [CONTENT_REFERENCE_TYPE_MARKETING_CENTERS]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
    ),
    [CONTENT_REFERENCE_TYPE_CATEGORY]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_CATEGORY,
    ),
    [CONTENT_REFERENCE_TYPE_FILM_SUBJECT]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_FILM_SUBJECT,
    ),
    [CONTENT_REFERENCE_TYPE_FILM_TYPE]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_FILM_TYPE,
    ),
    [CONTENT_REFERENCE_TYPE_ONBOARDING_CHOICES]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_ONBOARDING_CHOICES,
    ),
    [CONTENT_REFERENCE_TYPE_GENRES]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_GENRES,
    ),
    [CONTENT_REFERENCE_TYPE_YOGA_DURATION]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_YOGA_DURATION,
    ),
    [CONTENT_REFERENCE_TYPE_YOGA_FOCUS]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
    ),
    [CONTENT_REFERENCE_TYPE_YOGA_LEVEL]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
    ),
    [CONTENT_REFERENCE_TYPE_YOGA_STYLE]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_YOGA_STYLE,
    ),
    [CONTENT_REFERENCE_TYPE_FITNESS_DURATION]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_FITNESS_DURATION,
    ),
    [CONTENT_REFERENCE_TYPE_FITNESS_LEVEL]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_FITNESS_LEVEL,
    ),
    [CONTENT_REFERENCE_TYPE_FITNESS_SPECIALTY]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_FITNESS_SPECIALTY,
    ),
    [CONTENT_REFERENCE_TYPE_FITNESS_STYLE]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_FITNESS_STYLE,
    ),
    [CONTENT_REFERENCE_TYPE_CONSCIOUS_FORMAT]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_CONSCIOUS_FORMAT,
    ),
    [CONTENT_REFERENCE_TYPE_CONSCIOUS_TOPIC]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_CONSCIOUS_TOPIC,
    ),
    [CONTENT_REFERENCE_TYPE_PRODUCT]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_PRODUCT,
    ),
    [CONTENT_REFERENCE_TYPE_YOGA_TEACHER]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
    ),
    [CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_FITNESS_INSTRUCTOR,
    ),
    [CONTENT_REFERENCE_TYPE_INTERVIEW_HOST]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
    ),
    [CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
    ),
    [CONTENT_REFERENCE_TYPE_VIDEO_COLLECTION]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_VIDEO_COLLECTION,
    ),
    [CONTENT_REFERENCE_TYPE_VIDEO_TOPIC]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_VIDEO_TOPIC,
    ),
    [CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_SIMILAR_VIDEOS,
    ),
    [CONTENT_REFERENCE_TYPE_RECIPE_TYPE]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_RECIPE_TYPE,
    ),
    [CONTENT_REFERENCE_TYPE_RECIPE_DIET]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_RECIPE_DIET,
    ),
    [CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
    ),
    [CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
    ),
    [CONTENT_REFERENCE_TYPE_MEDITATION_STYLE]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
    ),
    [CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
    ),
    [CONTENT_REFERENCE_TYPE_MEDITATION_DURATION]: getContentReferenceValuesByType(
      content,
      CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
    ),
  }
}

/**
 * getInitialValues: Returns the initial values for Formik forms for all content types.
 * For video and series, featureMedia and previewMedia are objects { contentId, mediaId } to support UI and backend requirements.
 */
export function getInitialValues (content, section = null) {
  const contentReferences = getContentReferences(content)
  switch (defaultTo(section, content.get('contentType'))) {
    case 'content':
      return {
        contentSearchNoIndex: false,
        excludeFromRecommendations: false,
        path: '',
        title: content.get('title'),
        shortDescription: '',
        shortDescriptionFormat: '',
        description: '',
        descriptionFormat: '',
        thirdPartyDescription: '',
      }
    case CONTENT_TYPE_SERIES:
      return {
        contentId: content.get('contentId'),
        contentType: content.get('contentType'),
        language: content.get('language'),
        primaryLanguage: content.get('primaryLanguage'),
        contentSearchNoIndex: Boolean(content.get('contentSearchNoIndex')),
        excludeFromRecommendations: Boolean(content.get('excludeFromRecommendations')),
        path: content.get('path') || '',
        title: content.get('title'),
        shortDescription: content.get('shortDescription') || '',
        shortDescriptionFormat: content.get('shortDescriptionFormat') || '',
        description: content.get('description') || '',
        descriptionFormat: content.get('descriptionFormat') || '',
        thirdPartyDescription: content.get('thirdPartyDescription') || '',
        createdAt: content.get('createdAt'),
        updatedAt: content.get('updatedAt'),
        productType: content.get('productType') || '',
        displayType: content.get('displayType') || '',
        productionStatus: content.get('productionStatus') || '',
        episodeNumber: content.get('episodeNumber') || '',
        seasonNumber: content.get('seasonNumber') || '',
        instructor: content.get('instructor') || '',
        director: content.get('director') || '',
        producer: content.get('producer') || '',
        writer: content.get('writer') || '',
        cast: content.get('cast') || '',
        copyright: content.get('copyright') || '',
        studio: content.get('studio') || '',
        seoTitle: content.get('seoTitle') || '',
        seoDescription: content.get('seoDescription') || '',
        seoKeywords: content.get('seoKeywords') || '',
        seoRobotsNoFollow: content.get('seoRobotsNoFollow') || false,
        seoRobotsNoIndex: content.get('seoRobotsNoIndex') || false,
        published: content.get('published') || false,
        publishedAtDate: content.get('publishedAtDate') || '',
        featureMediaId: content.get('featureMediaId') || null,
        previewMediaId: content.get('previewMediaId') || null,
        ...contentReferences,
        generatePath: true,
        contentPolicyPolicyId: content.get('contentPolicyPolicyId') || '',
      }
    // TODO Check and clean this up
    case CONTENT_TYPE_FAQ:
    case CONTENT_TYPE_GUIDE:
    case CONTENT_TYPE_GUIDE_DAY:
    case CONTENT_TYPE_PERSON:
    case CONTENT_TYPE_ARTICLE:
      return {
        contentId: content.get('contentId'),
        contentType: content.get('contentType'),
        language: content.get('language'),
        primaryLanguage: content.get('primaryLanguage'),
        contentSearchNoIndex: Boolean(content.get('contentSearchNoIndex')),
        excludeFromRecommendations: Boolean(content.get('excludeFromRecommendations')),
        path: content.get('path') || '',
        title: content.get('title'),
        shortDescription: content.get('shortDescription') || '',
        shortDescriptionFormat: content.get('shortDescriptionFormat') || '',
        description: content.get('description') || '',
        descriptionFormat: content.get('descriptionFormat') || '',
        thirdPartyDescription: content.get('thirdPartyDescription') || '',
        createdAt: content.get('createdAt'),
        updatedAt: content.get('updatedAt'),
        productType: content.get('productType') || '',
        displayType: content.get('displayType') || '',
        episodeNumber: content.get('episodeNumber') || '',
        seasonNumber: content.get('seasonNumber') || '',
        instructor: content.get('instructor') || '',
        director: content.get('director') || '',
        producer: content.get('producer') || '',
        writer: content.get('writer') || '',
        cast: content.get('cast') || '',
        copyright: content.get('copyright') || '',
        studio: content.get('studio') || '',
        seoTitle: content.get('seoTitle') || '',
        seoDescription: content.get('seoDescription') || '',
        seoKeywords: content.get('seoKeywords') || '',
        seoRobotsNoFollow: content.get('seoRobotsNoFollow') || false,
        seoRobotsNoIndex: content.get('seoRobotsNoIndex') || false,
        published: content.get('published') || false,
        publishedAtDate: content.get('publishedAtDate') || '',
        ...contentReferences,
        generatePath: true,
        filterType: content.get('filterType') || '',
        bundle: content.get('bundle') || '',
      }
    case CONTENT_TYPE_TAG:
      return {
        contentId: content.get('contentId'),
        contentType: content.get('contentType'),
        language: content.get('language'),
        primaryLanguage: content.get('primaryLanguage'),
        contentSearchNoIndex: Boolean(content.get('contentSearchNoIndex')),
        excludeFromRecommendations: Boolean(content.get('excludeFromRecommendations')),
        path: content.get('path') || '',
        title: content.get('title'),
        shortDescription: content.get('shortDescription') || '',
        shortDescriptionFormat: content.get('shortDescriptionFormat') || '',
        description: content.get('description') || '',
        descriptionFormat: content.get('descriptionFormat') || '',
        thirdPartyDescription: content.get('thirdPartyDescription') || '',
        createdAt: content.get('createdAt'),
        updatedAt: content.get('updatedAt'),
        productType: content.get('productType') || '',
        displayType: content.get('displayType') || '',
        episodeNumber: content.get('episodeNumber') || '',
        seasonNumber: content.get('seasonNumber') || '',
        instructor: content.get('instructor') || '',
        director: content.get('director') || '',
        producer: content.get('producer') || '',
        writer: content.get('writer') || '',
        cast: content.get('cast') || '',
        copyright: content.get('copyright') || '',
        studio: content.get('studio') || '',
        seoTitle: content.get('seoTitle') || '',
        seoDescription: content.get('seoDescription') || '',
        seoKeywords: content.get('seoKeywords') || '',
        seoRobotsNoFollow: content.get('seoRobotsNoFollow') || false,
        seoRobotsNoIndex: content.get('seoRobotsNoIndex') || false,
        published: content.get('published') || false,
        publishedAtDate: content.get('publishedAtDate') || '',
        ...contentReferences,
        generatePath: true,
        listCriteria: getListCriteriaInitialValue(content),
        filterType: content.get('filterType') || '',
        bundle: content.get('bundle') || '',
      }
    case CONTENT_TYPE_PROMOTION:
    case CONTENT_TYPE_PRODUCT:
    case CONTENT_TYPE_BONUS_PACKAGE:
    case CONTENT_TYPE_SERVICE:
      return {
        contentId: content.get('contentId'),
        contentType: content.get('contentType'),
        language: content.get('language'),
        primaryLanguage: content.get('primaryLanguage'),
        contentSearchNoIndex: Boolean(content.get('contentSearchNoIndex')),
        excludeFromRecommendations: Boolean(content.get('excludeFromRecommendations')),
        path: content.get('path') || '',
        title: content.get('title'),
        shortDescription: content.get('shortDescription') || '',
        shortDescriptionFormat: content.get('shortDescriptionFormat') || '',
        description: content.get('description') || '',
        descriptionFormat: content.get('descriptionFormat') || '',
        thirdPartyDescription: content.get('thirdPartyDescription') || '',
        createdAt: content.get('createdAt'),
        updatedAt: content.get('updatedAt'),
        productType: content.get('productType') || '',
        displayType: content.get('displayType') || '',
        episodeNumber: content.get('episodeNumber') || '',
        seasonNumber: content.get('seasonNumber') || '',
        instructor: content.get('instructor') || '',
        director: content.get('director') || '',
        producer: content.get('producer') || '',
        writer: content.get('writer') || '',
        cast: content.get('cast') || '',
        copyright: content.get('copyright') || '',
        studio: content.get('studio') || '',
        seoTitle: content.get('seoTitle') || '',
        seoDescription: content.get('seoDescription') || '',
        seoKeywords: content.get('seoKeywords') || '',
        seoRobotsNoFollow: content.get('seoRobotsNoFollow') || false,
        seoRobotsNoIndex: content.get('seoRobotsNoIndex') || false,
        ctaLabel: content.get('ctaLabel') || '',
        ctaPath: content.get('ctaPath') || '',
        ctaDeeplinkPath: content.get('ctaDeeplinkPath') || '',
        published: content.get('published') || false,
        publishedAtDate: content.get('publishedAtDate') || '',
        ...contentReferences,
        generatePath: true,
        contentPolicyPolicyId: content.get('contentPolicyPolicyId') || '',
      }
    case CONTENT_TYPE_VIDEO:
      return {
        contentId: content.get('contentId'),
        contentType: content.get('contentType'),
        language: content.get('language'),
        primaryLanguage: content.get('primaryLanguage'),
        contentSearchNoIndex: Boolean(content.get('contentSearchNoIndex')),
        excludeFromRecommendations: Boolean(content.get('excludeFromRecommendations')),
        path: content.get('path') || '',
        title: content.get('title'),
        shortDescription: content.get('shortDescription') || '',
        shortDescriptionFormat: content.get('shortDescriptionFormat') || '',
        description: content.get('description') || '',
        descriptionFormat: content.get('descriptionFormat') || '',
        thirdPartyDescription: content.get('thirdPartyDescription') || '',
        createdAt: content.get('createdAt'),
        updatedAt: content.get('updatedAt'),
        productType: content.get('productType'),
        displayType: content.get('displayType'),
        episodeNumber: content.get('episodeNumber') || '',
        seasonNumber: content.get('seasonNumber') || '',
        instructor: content.get('instructor') || '',
        director: content.get('director') || '',
        producer: content.get('producer') || '',
        writer: content.get('writer') || '',
        cast: content.get('cast') || '',
        copyright: content.get('copyright') || '',
        studio: content.get('studio') || '',
        seoTitle: content.get('seoTitle') || '',
        seoDescription: content.get('seoDescription') || '',
        seoKeywords: content.get('seoKeywords') || '',
        seoRobotsNoFollow: content.get('seoRobotsNoFollow') || false,
        seoRobotsNoIndex: content.get('seoRobotsNoIndex') || false,
        published: content.get('published') || false,
        publishedAtDate: content.get('publishedAtDate') || '',
        featureMediaId: content.get('featureMediaId') || null,
        previewMediaId: content.get('previewMediaId') || null,
        ...contentReferences,
        [CONTENT_REFERENCE_TYPE_SERIES]: getContentReferenceValueByType(
          content,
          CONTENT_REFERENCE_TYPE_SERIES,
        ),
        generatePath: true,
      }
    default:
      return {}
  }
}
